import {
   assign,
   clone,
   cloneDeep,
   filter,
   find,
   get,
   isEmpty,
   isUndefined,
   keys,
   lowerCase,
   map,
   omitBy,
   pick,
   replace,
   size,
   split,
   template,
   trim,
} from 'lodash';
import moment from 'moment-timezone';
import { FAQS, FAQS_COST_SAVING, MAX_ROOMS, MAX_TOTAL_ADULTS, taxesAndFees } from './constants';
import { processFacilities, starsDefault } from './hotelFilter';
import { propertySleepsAtLeast } from './plannerRooms.util';
import timespace from '@mapbox/timespace';
import { mixed, number, string } from 'yup';
import { FileSize } from './file.util';
import { ImageMimeType, VideoType } from './image.util';
import i18n from 'src/locales';

export const deduplicateCommaString = (value: string | null | undefined): string => {
   return (
      value
         ?.split(',')
         .map((s: string) => s.trim())
         .filter((s: string, idx: number, arr: string[]) => arr.indexOf(s) === idx)
         .join(', ') || ''
   );
};

export const deduplicateByKey = (items: any[], keyExtractor: (item: any) => string): any[] => {
   return Array.from(
      new Map(items.map((item: any) => [keyExtractor(item), item])).values()
   );
};

export const deduplicateArrayStrings = (arr: string[], caseInsensitive = false): string[] => {
   if (!arr?.length) return [];
   if (!caseInsensitive) return Array.from(new Set(arr));

   const seen = new Map<string, string>();
   arr.forEach(item => {
      const key = lowerCase(item?.trim() || '');
      if (!seen.has(key)) seen.set(key, item);
   });
   return Array.from(seen.values());
};

export const convertFileSize2Bytes = (size: string): number | null => {
   const units: { [key: string]: number } = {
      B: 1,
      KB: 1024,
      MB: 1024 * 1024,
      GB: 1024 * 1024 * 1024,
      TB: 1024 * 1024 * 1024 * 1024,
   };

   const match = size.match(/^(\d+(\.\d+)?)\s*([KMGT]?B)$/i);
   if (!match) {
      console.error('Invalid file size string format.');
      return null;
   }

   const value = parseFloat(match[1]);
   const unit = match[3].toUpperCase();

   if (isNaN(value) || !(unit in units)) {
      console.error('Could not parse file size value or unit.');
      return null;
   }

   return value * units[unit];
};

export const convertFileSize2String = (bytes: number): string => {
   if (typeof bytes !== 'number' || isNaN(bytes) || bytes < 0) {
      throw new Error('Invalid byte value');
   }

   const units = ['B', 'KB', 'MB', 'GB', 'TB'];
   let index = 0;

   while (bytes >= 1024 && index < units.length - 1) {
      bytes /= 1024;
      index++;
   }

   return `${bytes.toFixed(2)} ${units[index]}`;
};

export const filterUndefined = (arr: any) => omitBy(arr, isUndefined);

export const haversine = (location1: any, location2: any) => {
   if (!location1 || !location2) return 0;
   const { latitude: lat1, longitude: lon1 } = location1;
   const { latitude: lat2, longitude: lon2 } = location2;
   const toRad = (deg: number) => deg * (Math.PI / 180);
   const R = 6371;
   const dLat = toRad(lat2 - lat1);
   const dLon = toRad(lon2 - lon1);
   const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
   return R * c;
};

// The old price slider topped out here, and a max at the top meant "no max".
const LEGACY_AMOUNT_SLIDER_MAX = 500;

/**
 * NPT-1721: `minAmount`/`maxAmount` are the URL params of the retired price slider. They were
 * compared against `totalPayment.taxDueSupplier` (tax paid at the hotel, nearly always 0), so a
 * minimum emptied the results and a maximum did nothing. Old links and bookmarks still carry
 * them, so they are read as the budget filter they meant (a nightly price per room) unless the
 * URL already has a budget, and are never passed on.
 */
export const translateLegacyBudget = <T extends Record<string, any>>(params: T): T => {
   if (!params || (params.minAmount === undefined && params.maxAmount === undefined)) return params;
   const { minAmount, maxAmount, ...rest } = params;
   const present = (value: unknown) => value !== undefined && value !== null && value !== '';
   if (present(rest.budgetMin) || present(rest.budgetMax)) return rest as unknown as T;
   const min = Number(minAmount);
   const max = Number(maxAmount);
   return omitBy(
      {
         ...rest,
         budgetMin: min > 0 ? String(min) : undefined,
         budgetMax: max > 0 && max < LEGACY_AMOUNT_SLIDER_MAX ? String(max) : undefined,
      },
      value => value === undefined,
   ) as unknown as T;
};

export const renderRequestFilter = (rawParams: any) => {
   const params = translateLegacyBudget(rawParams ?? {});
   const {
      range,
      rooms,
      adults,
      yearOlds,
      center,
      countryCode = 'us',
      favoritesOnly,
      amenities,
      stars,
      chains,
      refunds,
      guestRating,
      propertyTypes,
      budgetMin,
      budgetMax,
      beds,
      isPet,
      place_name,
      gameId,
   } = params;
   const arrCenter = center ? map(split(center, ','), c => Number(c)) : null;
   const locationPayload = arrCenter ? { longitude: arrCenter[0], latitude: arrCenter[1], countryCode } : undefined;
   let timePayload = { checkin: '', checkout: '' };
   const rangeList = split(range, ',');
   if (!!range && size(rangeList) === 2) {
      timePayload = { checkin: rangeList[0], checkout: rangeList[1] };
   }

   const roomNumber = Math.min(Number(rooms || 1), MAX_ROOMS);
   // BH Search API criteria: adults is a total and must be >= rooms (max 36).
   const adultNumber = Math.min(Math.max(Number(adults || 2), roomNumber), MAX_TOTAL_ADULTS);
   const children = map(
      filter(split(yearOlds, ','), item => !!item),
      item => Number(item),
   );

   const query = filterUndefined({
      favoritesOnly: favoritesOnly || undefined,
      amenities: amenities ? split(amenities, ',') : undefined,
      refunds: refunds ? split(refunds, ',') : undefined,
      chains: chains ? split(chains, ',') : undefined,
      stars: stars ? map(split(stars, ','), Number) : undefined,
      guestRating: guestRating ? split(guestRating, ',') : undefined,
      // Property type names can themselves contain a comma (e.g. "Apartment/Flat, Hotel"),
      // so `,` can't double as the multi-value delimiter here — use `|` instead.
      propertyTypes: propertyTypes ? split(propertyTypes, '|') : undefined,
      budgetMin: budgetMin != null && budgetMin !== '' ? Number(budgetMin) : undefined,
      budgetMax: budgetMax != null && budgetMax !== '' ? Number(budgetMax) : undefined,
      beds: beds ? Number(beds) : undefined,
   });
   // Pass the human-readable place name through to the backend for analytics —
   // Mapbox returns it pre-formatted (e.g. "Austin, Texas, United States") so we
   // send the whole thing as searchCityName and let AC display it as-is.
   const searchCityName: string | undefined = place_name ? String(place_name).split(',')[0]?.trim() : undefined;
   const searchStateName: string | undefined = place_name ? String(place_name).split(',')[1]?.trim() : undefined;

   // gameId is only present when this search was reached from a game/planner context
   // (view-all-hotels CTA). Sending it lets the API pin that game's curated TEAM/BUDGET/LUXURY
   // hotels to the top of the results (NPT-1728); a direct/generic search omits it entirely
   // rather than sending an empty string, which would be a no-op pin on the API side anyway.
   const payload = assign({}, locationPayload, timePayload, {
      roomNumber,
      adultNumber,
      children,
      isPet,
      searchCityName,
      searchStateName,
      gameId: gameId || undefined,
   });
   return { query, payload };
};

// Mirrors the reverse-geocode call in src/api/bookingApi.ts's getLocation() (same
// endpoint/params), but takes an explicit lng/lat instead of reading navigator.geolocation
// — used to label the map center after a pan/zoom instead of the user's device location.
export const reverseGeocodePlaceName = async (lng: number, lat: number): Promise<string | null> => {
   const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
   if (!token) return null;
   try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&types=place,locality,district,region&limit=1`;
      const res = await fetch(url);
      const data = await res.json();
      const feature = data?.features?.[0];
      if (!feature) return null;
      const region = find(feature.context, (c: any) => typeof c.id === 'string' && c.id.startsWith('region'));
      return region ? `${feature.text}, ${region.text}` : feature.text;
   } catch {
      return null;
   }
};

export const getBodyParams = (params: any) =>
   pick(params, ['range', 'place_name', 'center', 'countryCode', 'rooms', 'adults', 'yearOlds', 'isPet', 'radius', 'feature_type', 'poi_category']);

export const getQueryParams = (params: any) =>
   translateLegacyBudget(
      pick(params, ['amenities', 'stars', 'chains', 'minAmount', 'maxAmount', 'budgetMin', 'budgetMax', 'refunds', 'guestRating', 'propertyTypes', 'favoritesOnly', 'beds']),
   );

export const getRoomFilter = (allParams: any) => {
   const adults = allParams.adults ? Number(allParams.adults) : undefined;
   const children = allParams.children ? Number(allParams.children) : undefined;
   const yearOlds = allParams.yearOlds ? split(allParams.yearOlds, ',').map(item => (item ? Number(item) : null)) : undefined;
   const rooms = allParams.rooms ? Number(allParams.rooms) : undefined;
   const isPet = allParams.isPet ? true : undefined;
   return omitBy({ adults, children, yearOlds, rooms, isPet }, value => !value);
};

export const reviewScore = (review: any) => {
   if (!review?.rating) return '';
   if (review.rating >= 4.5) return 'Exceptional';
   if (review.rating >= 4.0 && review.rating < 4.5) return 'Wonderful';
   if (review.rating >= 3.5 && review.rating < 4.0) return 'Very Good';
   if (review.rating >= 3.0 && review.rating < 3.5) return 'Good';
   return 'Review score';
};

export type HotelRatingLabel = {
   labelKey: string;
   className: string;
};

export const getHotelRatingLabel = (rating: number | string | null | undefined): HotelRatingLabel | null => {
   const score = typeof rating === 'string' ? parseFloat(rating) : rating;
   if (score == null || isNaN(score as number)) return null;
   if ((score as number) >= 4.7) {
      return { labelKey: 'rating.amazing', className: 'bg-gradient-to-r from-amber-400 to-orange-500 text-white' };
   }
   if ((score as number) >= 4.4) {
      return { labelKey: 'rating.excellent', className: 'bg-emerald-500 text-white' };
   }
   if ((score as number) >= 4.0) {
      return { labelKey: 'rating.great', className: 'bg-sky-500 text-white' };
   }
   return null;
};

export const getPath = (path: any, params: any = {}) => {
   let clonePath = clone(path);
   keys(params).forEach(key => {
      clonePath = replace(clonePath, `:${key}`, params[key]);
   });
   return clonePath;
};

export const updateImages = (ht: any, data: any) => {
   if (!data) return;
   ht.hotelImages = data?.[ht.id] || ht.hotelImages;
};

export const updateReviews = (ht: any, data: any) => {
   if (!data) return;
   ht.hotelReviews = data?.[ht.id]?.[0] || ht.hotelReviews;
};

export const convertTo12Hour = (dateTime: string): string => {
   if (!dateTime) {
      return dateTime;
   }

   // Expect dateTime in ISO format (YYYY-MM-DDTHH:MM:SS.000Z) and extract only the time part (HH:MM:SS) without converting timezone
   const time = dateTime.split('T')[1].replace('.000Z', '');

   const [hoursStr, minutes, _seconds] = time.split(':');
   let hours = parseInt(hoursStr, 10);

   const period = hours >= 12 ? 'PM' : 'AM';
   hours = hours % 12 || 12; // 0 -> 12

   return `${hours}:${minutes} ${period}`;
};

export const numberFormat = (amount: number, currency = 'USD', digit = 2) => {
   if (isNaN(amount) || amount === 0) return '';
   try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: digit }).format(amount);
   } catch {
      return `${currency} ${amount.toFixed(digit)}`;
   }
};

export const numberFormatForMap = (amountstr: any, currency = 'USD') => {
   const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).formatToParts(1);
   const cur = parts.find(p => p.type === 'currency')?.value || '$';
   const amount = Number(amountstr);
   if (!amount) {
      return 0;
   } else if (amount < 1000) {
      return `${cur}${Math.round(amount)}`;
   } else if (amount < 1000000) {
      return `${cur}${Number(trim((amount / 1000).toFixed(1), '.'))}K`;
   } else {
      return `${cur}${Number(trim((amount / 1000000).toFixed(1), '.'))}M`;
   }
};

export const handleFilterHotel = (filterQuery: any, ht: any) => {
   let newHotel = cloneDeep(ht);

   if (isEmpty(filterQuery)) return newHotel;
   const { stars = starsDefault, chains = [], amenities = [], refunds = [], guestRating = [], propertyTypes = [], budgetMin, budgetMax, searchText, rooms, minStarRating, beds } =
      translateLegacyBudget(filterQuery);
   const roomCount = Number(rooms) > 0 ? Number(rooms) : 1;
   const minBeds = Number(beds) > 0 ? Number(beds) : 0;

   const handleFilterByFacilities = (facilities: string[]) => {
      if (amenities.length === 0) return true;
      if (!facilities?.length) return true;

      const mappedFacilities = processFacilities(facilities)?.map(f => f.display) || [];

      return amenities.every((f: string) => mappedFacilities.includes(f));
   };

   const handleFilterByRefunds = (isFullyRefundable: boolean) => {
      switch (true) {
         case refunds.includes('refundableRatesVailable'):
            return isFullyRefundable;

         default:
            return true;
      }
   };

   // Travelers shopping for a room that sleeps a family search on bed count, not star rating.
   // Judged against the hotel's STATIC room-type catalogue, never against `rates`: this list
   // holds one rate per hotel (BH caps ratesPerProperty at 1 for the batch sizes search uses),
   // so the rate on hand says nothing about the rest of the property. See propertySleepsAtLeast
   // for why an unreadable catalogue keeps the hotel instead of hiding it.
   const handleFilterByBeds = (bedTypes: unknown) => propertySleepsAtLeast(bedTypes, minBeds);

   const handleFilterByGuestRating = (rating: any) => {
      if (isEmpty(guestRating)) return true;
      const numericRating = Number(rating);
      if (isNaN(numericRating)) return true;
      const minRating = Number(guestRating[0]);
      return numericRating >= minRating;
   };

   newHotel = filter(newHotel, ht => {
      const { star_rating, hotel_chain, name, kind, facilities } = ht.hotel;

      // NPT-1721: a hotel can arrive with `rates: null`. It is excluded by the `ht.rates !== null`
      // check below, but this line runs first, so it must not assume an array.
      const isFullyRefundable = Array.isArray(ht.rates) && ht.rates.some((r: any) => r.freeCancellation);

      const guestRating = ht.rating?.rating;

      const nightlyRate = ht.rates?.[0]?.commission?.nightlyRate;
      const roomNightlyRate = nightlyRate != null ? nightlyRate / roomCount : null;
      const budgetMatch = ht.priceStatus === 'loading' || (!budgetMin && !budgetMax) ||
         (roomNightlyRate != null && (roomNightlyRate >= budgetMin || !budgetMin) && (roomNightlyRate < budgetMax || !budgetMax));

      // NPT-1727. The `|| 0` is deliberate: a hotel with no star rating counts as
      // below an org's floor rather than being waved through. Same rule, and the
      // same warning against "fixing" it, as passesPreferences in
      // plannerHotelPicks.util.ts — read the note there before changing either.
      const numericStarRating = Number(star_rating) || 0;
      const passesMinStarRating = !minStarRating || numericStarRating >= Number(minStarRating);

      return (
         passesMinStarRating &&
         handleFilterByFacilities(facilities) &&
         (ht.priceStatus === 'loading' || handleFilterByRefunds(isFullyRefundable)) &&
         // No priceStatus guard: bedding is static, so this answers the same whether or not
         // prices have landed.
         handleFilterByBeds(ht.hotel?.bed_types) &&
         handleFilterByGuestRating(guestRating) &&
         budgetMatch &&
         stars.some((s: any) => String(s) === String(star_rating)) &&
         (isEmpty(chains) || chains.some((c: string) => lowerCase(hotel_chain?.trim() || '') === lowerCase(c?.trim() || ''))) &&
         (isEmpty(propertyTypes) || propertyTypes.some((pt: string) => lowerCase(deduplicateCommaString(kind)) === lowerCase(pt?.trim() || ''))) &&
         (lowerCase(hotel_chain).includes(lowerCase(searchText)) || lowerCase(name).includes(lowerCase(searchText))) &&
         ht.rates !== null
      );
   });

   return newHotel;
};

export const showNumberNight = ([start, end]: string[] = []) => {
   const numberNight = moment(end).diff(moment(start), 'days');
   // Callers on pages without a date range in the URL (e.g. My Favorites visited directly,
   // no ?range=) pass through split(undefined, ',') === [''] — moment(undefined) is "now" and
   // moment('') is invalid, so the diff is NaN. Hide the summary instead of showing "NaN nights".
   if (!Number.isFinite(numberNight)) return '';
   return `${numberNight} ${numberNight === 1 || numberNight.toString() === '1' ? 'night' : 'nights'}`;
};

// Game/event time is stored as 24h "HH:mm" / "HH:mm:ss" (e.g. "15:30:00"); show it as "3:30 PM".
export const formatGameTime = (time?: string): string | undefined => {
   const m = time && /^(\d{1,2}):(\d{2})/.exec(time);
   if (!m) return time;
   const h = Number(m[1]);
   const ampm = h >= 12 ? 'PM' : 'AM';
   return `${h % 12 || 12}:${m[2]} ${ampm}`;
};

export const showRooms = (rooms: number | string = 1) => i18n.t('homeFilter.summary.rooms', { count: Number(rooms) });

export const showAdults = (adults: number | string = 2) => i18n.t('homeFilter.summary.adults', { count: Number(adults) });

export const showChildren = (children: number | string = 0) => i18n.t('homeFilter.summary.children', { count: Number(children) || 0 });

// Guest-selector pill label. Adults/children are totals across all rooms
// (NPT-1418) — the supplier distributes guests, so "3 rooms · 6 adults + 3 children".
export const getRoomSummaryLabel = (roomFilter: any) => {
   const adults = Number(roomFilter?.adults || 2);
   const children = Number(roomFilter?.children || 0);
   const rooms = Math.min(Number(roomFilter?.rooms || 1), MAX_ROOMS);
   const guests = [showAdults(adults), children > 0 ? showChildren(children) : null].filter(Boolean).join(' + ');
   return `${showRooms(rooms)} · ${guests}${roomFilter?.isPet ? ` · ${i18n.t('homeFilter.summary.pets')}` : ''}`;
};

export const convertToShort24Hour = (time: string): string => {
   if (!time) {
      return time;
   }
   const [hours, minutes] = time.split(':');

   return `${hours}:${minutes}`;
};

export const getTaxesFees = (rate: any, isIncluded?: any) => {
   let taxes: any = rate?.payment_options?.payment_types?.[0]?.tax_data?.taxes;
   taxes = isIncluded !== undefined ? filter(taxes, tax => tax.included_by_supplier == !!isIncluded) : taxes;
   return taxes;
};

export const showTaxesFeesNoIncluded = (rate: any) => {
   const taxes: any = getTaxesFees(rate, false);
   if (isEmpty(taxes)) return 'Included taxes and fees';
   return map(taxes, tax => `${numberFormat(tax.amount, tax.currency_code)} ${get(taxesAndFees, tax.name, '')}`).join(' + ');
};

export const showExcludedTaxAndFee = (exTaxes: any, taxNames: any) => {
   return keys(exTaxes)
      .map((key: any) => `${numberFormat(exTaxes[key].amount, exTaxes[key].currency)} ${get(taxNames, key, '')}`)
      .join(' + ');
};

export const showExcludedTaxAndFeeTotal = (exTaxes: { amount: string; currency: string }[]) => {
   const total = Object.values(exTaxes).reduce((sum: number, item) => sum + Number(item?.amount), 0);
   const currency = (Object.values(exTaxes)[0] as { currency: string })?.currency;
   return numberFormat(total, currency);
};

export const showIncludedTaxAndFee = (inTaxes: any) => {
   return `${numberFormat(inTaxes)} Included taxes and fees`;
};

export const timeByLocation = (time: moment.MomentInput, point: [number, number]): moment.Moment => {
   // A hotel without coordinates has no timezone to resolve, and getFuzzyLocalTimeFromPoint
   // returns undefined rather than throwing — reading `.tz()` off it took the whole page down
   // (no error boundary above the rate list). Fall back to the viewer's own zone.
   const [longitude, latitude] = point ?? [];
   if (!Number.isFinite(Number(longitude)) || !Number.isFinite(Number(latitude))) return moment(time);
   const zone = timespace.getFuzzyLocalTimeFromPoint(Date.now(), [Number(longitude), Number(latitude)])?.tz();
   return zone ? moment.tz(time, zone) : moment(time);
};

export const formatDate = (date: Date, type?: string): string => {
   return moment(date).format(type ?? 'MMM DD, YYYY');
};

/**
 * NPT-1728 — buckets TEAM/BUDGET/LUXURY-designated hotels to the front, TEAM first, then BUDGET,
 * then LUXURY, then everything else. Call this AFTER the caller's own sort (Distance/Recommended/
 * Best rating, or the map sidebar's price sort): Array.prototype.sort is stable, so each tier
 * keeps that sort's internal order — this only regroups by tier, it never reorders within one.
 *
 * `getFlags` defaults to reading the flags off the item itself (hotelSearch's flat hotel objects);
 * pass an accessor for a wrapper shape — the map sidebar's `SidebarCard` carries them on `.item`.
 */
export const pinDesignatedHotels = <T,>(
   items: T[],
   getFlags: (item: T) => { isTeamHotel?: boolean; isBudgetPick?: boolean; isLuxuryPick?: boolean } | undefined = (item: any) => item,
): T[] => {
   const tierRank = (item: T) => {
      const flags = getFlags(item);
      return flags?.isTeamHotel ? 0 : flags?.isBudgetPick ? 1 : flags?.isLuxuryPick ? 2 : 3;
   };
   return [...items].sort((a, b) => tierRank(a) - tierRank(b));
};

export const getDataHotels = (hotels: any, images: any) => {
   const newHotels = cloneDeep(hotels);

   return map(newHotels, hotel => {
      const hotelId = hotel?.hotel?.id ?? hotel?.id;
      const hotelImages = images?.[hotelId];

      return assign({}, hotel, { hotelImages });
   });
};

export const formatTaxName = (key: string) => {
   return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export const verifyUpload = () =>
   mixed()
      .optional()
      .nullable()
      .test('fileSize', `File size is too large. Recommended file size is ${convertFileSize2String(FileSize.MEDIUM)}`, (value: any) => {
         if (!value || typeof value === 'string') return true;
         return value.size <= FileSize.MEDIUM;
      })
      .test('fileType', 'File type is not supported', (value: any) => {
         if (!value || typeof value === 'string') return true;
         return [ImageMimeType.SVG, ImageMimeType.PNG, ImageMimeType.JPG, ImageMimeType.JPEG].includes(value.type as ImageMimeType);
      });

export const verifyUploadVideo = (t: any, payload?: { request?: boolean }) => {
   const { request } = payload || {};

   let vUploadVideo = mixed()
      .nullable()
      .test('fileSize', `File size is too large. Recommended file size is ${convertFileSize2String(FileSize.EXTRA_LARGE)}`, (value: any) => {
         if (!value || typeof value === 'string') return true;
         return value.size <= FileSize.EXTRA_LARGE;
      })
      .test('fileType', 'File type is not supported', (value: any) => {
         if (!value || typeof value === 'string') return true;
         return [VideoType.MP4, VideoType.WEBM].includes(value.type as VideoType);
      });

   vUploadVideo = request
      ? vUploadVideo
           .required(t('validations.required'))
           .test('notEmpty', t('validations.required'), (value: any) => (typeof value === 'string' ? value.trim().length > 0 : !!value))
      : vUploadVideo.optional().nullable();

   return vUploadVideo;
};

export const verifyImageUploadVideo = (t: any, payload?: { request?: boolean }) => {
   const { request } = payload || {};

   let vUploadVideo = mixed()
      .nullable()
      .test('fileSize', `File size is too large. Recommended file size is ${convertFileSize2String(FileSize.EXTRA_LARGE)}`, (value: any) => {
         if (!value || typeof value === 'string') return true;
         return (value.size || value.fileSize) <= FileSize.EXTRA_LARGE;
      })
      .test('fileType', 'File type is not supported', (value: any) => {
         if (!value || typeof value === 'string') return true;
         return [VideoType.MP4, VideoType.WEBM, ImageMimeType.SVG, ImageMimeType.PNG, ImageMimeType.JPG, ImageMimeType.JPEG].includes(
            value.type || value.fileType,
         );
      });

   vUploadVideo = request
      ? vUploadVideo
           .required(t('validations.required'))
           .test('notEmpty', t('validations.required'), (value: any) => (typeof value === 'string' ? value.trim().length > 0 : !!value))
      : vUploadVideo.optional().nullable();

   return vUploadVideo;
};

export const verifyString = (t: any, payload?: any) => {
   const { request, cRequest, max, cMax, matches, isUrl } = payload || {};
   let vString = string().nullable();

   if (request) vString = vString.required(t('validations.required'));

   if (max) vString = vString.max(max, t('common.input.maxLength', { number: max }));

   if (cMax) {
      vString = vString.test('custom-max', t('common.input.maxLength', { number: cMax }), (value: any) => {
         if (!value) return true; // allow null/empty
         return size(stripHtml(value)) <= cMax;
      });
   }
   if (cRequest) {
      vString = vString.test('c-required', t('validations.required'), value => !!value && !isEmpty(stripHtml(value)));
   }

   if (isUrl) {
      if (isUrl) vString = vString.url(t('validations.invalidUrl'));
   }

   if (matches)
      vString = vString.matches(
         /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
         'Slug can only contain lowercase letters, numbers, and hyphens (no spaces or special characters)',
      );
   return vString;
};

export const verifyNumber = (t: any, payload?: any) => {
   const { request, max } = payload || {};
   let vNumber = number();
   if (request) vNumber = vNumber.required(t('validations.required'));
   if (max) vNumber = vNumber.max(max, t('common.input.maxLength', { number: max }));
   return vNumber;
};

export const verifyEmail = (t: any, payload?: any) => {
   const { request } = payload || {};
   let vEmail = string().email();

   if (request) vEmail = vEmail.required(t('validations.required'));

   return vEmail;
};
export const verifySiteEmail = (t: any, options?: { request?: boolean }) => {
   const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

   let schema = string()
      .trim()
      .test('is-email', t('invalidEmail'), value => {
         if (!value) return true;
         return emailRegex.test(value);
      });

   if (options?.request) {
      schema = schema.required(t('validations.required'));
   }

   return schema;
};
export const checkFileStyle = (value: any) => value && (value instanceof File || value instanceof Blob);

export const stripHtml = (htmlString: string): any => {
   if (!htmlString) return htmlString;

   // Use regex instead of DOMParser for Node.js/SSR compatibility
   return htmlString
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace HTML entities
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .trim(); // Trim whitespace
};

export const isEditorEmpty = (value: string) => {
   if (!value) return true;
   return value?.replace(/<[^>]*>/g, '').trim() === '';
};

export const compilePath = (path: string) => {
   return new RegExp('^' + path.replace(/:[a-zA-Z]+/g, '([^/]+)') + '$');
};

// NPT-1731 — isCostSaving picks the savings-framed FAQ set so the default questions never
// claim proceeds/donations for an org that passes its margin back as a lower price instead.
export const getFaqDefaults = (organizationName: string, isCostSaving = false) =>
   (isCostSaving ? FAQS_COST_SAVING : FAQS).map(({ id, question, answer }) => ({
      id,
      question: template(question)({ organizationName }),
      answer: template(answer)({ organizationName }),
   }));

export const checkHtml = (text: string) => /<[a-z][\s\S]*>/i.test(text);

export const isVideoType = (type: string) => {
   return /^video\//.test(type);
};

export const isImageType = (type: string) => {
   return /^image\//.test(type);
};

export const isVideo = (url: string) => {
   return /\.(mp4|webm)$/i.test(url);
};

export const isImage = (url: string) => {
   return /\.(jpg|jpeg|png|svg)$/i.test(url);
};

// Guards against the checkout case where the city name is duplicated into the
// street address field (e.g. autocomplete bleed), which fails card AVS checks.
export const hasCityInStreetAddress = (street?: string, city?: string): boolean => {
   const streetLower = (street || '').toLowerCase().trim();
   const cityLower = (city || '').toLowerCase().trim();
   if (streetLower.length < 2 || cityLower.length < 2) return false;
   return streetLower.includes(cityLower) || cityLower.includes(streetLower);
};
